﻿export default class Accordion extends HyperHTMLElement {
	constructor(self) {
		self = super(self);
		self._contentNodes = [];
		self._headerNodes = [];
		// SteepGraph Modification
		// Developer    : Pratik Daud
		// Date         : 06/04/2026
		// Purpose      : Initialize maximize state handling for accordion component.
		// ==============================================
		// Changes Start
		self._readyForMaximize = false;
		//Changes End
		return self;
	}

	static get booleanAttributes() {
		return ['collapsed'];
	}

	_keyboardHandler(e) {
		const key = e.code;
		if (key === 'Enter' || key === 'Space') {
			e.preventDefault();
			this.toggle();
		}
	}

	created() {
		this.classList.add('aras-accordion');
		this.setAttribute('role', 'presentation');
	}

	appendChild(child) {
		if (this._isConnected && !this.hasChildNodes()) {
			return super.appendChild(child);
		}

		this._contentNodes.push(child);

		if (this._isConnected) {
			this.render();
		}

		return child;
	}

	removeChild(child) {
		if (this._contentNodes.indexOf(child) === -1) {
			return super.removeChild(child);
		}

		this._contentNodes = this._contentNodes.filter(
			(currentChild) => currentChild !== child
		);
		this.render();

		return child;
	}

	connectedCallback() {
		if (this._isConnected) {
			return;
		}

		this._isConnected = true;
		// SteepGraph Modification
		// Developer    : Pratik Daud
		// Date         : 06/04/2026
		// Purpose      : Ensure maximize button is initialized after DOM render.
		// ==============================================
		// Changes Start
		setTimeout(() => {
			this._readyForMaximize = true;
			this._syncMaximizeButton();
		}, 300);
               //Changes End
		const contentNodes = [];
		const headerNodes = [];

		// Detach live children BEFORE pushing to arrays
		const liveChildren = Array.from(this.children);
		for (const child of liveChildren) {
			this.removeChild(child); // detach from DOM first
		}

		const allChildren = [...liveChildren, ...this._contentNodes];
		for (const child of allChildren) {
			if (child.hasAttribute?.('slot') && child.getAttribute('slot') === 'header') {
				headerNodes.push(child);
			} else {
				contentNodes.push(child);
			}
		}
		this._contentNodes = contentNodes;
		this._headerNodes = headerNodes;

		this.render();
	}

	attributeChangedCallback() {
		if (!this._isConnected) {
			return;
		}

		this.render();

		requestAnimationFrame(() => {
			this._syncMaximizeButton();
		});
	}

	collapse() {
		return this.toggle(true);
	}

	expand() {
		return this.toggle(false);
	}

	render() {
		const className = `aras-accordion__toggle-button aras-icon-arrow aras-icon-arrow_${
			this.collapsed ? 'down' : 'up'
		}`;

		this.classList.toggle('aras-accordion_collapsed', this.collapsed);

		this.html`
			<div class="aras-accordion__header">
				<span
					class="${className}"
					onclick="${() => this.toggle()}"
					onkeydown="${(e) => this._keyboardHandler(e)}"
					role="button"
					aria-expanded="${!this.collapsed}"
					aria-label="${aras.getResource('', 'aria_label.ItemView.AccordionButton')}"
					tabIndex="0"
				></span>
				${this._headerNodes}
			</div>
			<div
				class="aras-accordion__content"
				role="region"
				aria-hidden="${this.collapsed}"
			>${this._contentNodes}</div>
		`;
		// SteepGraph Modification
		// Developer    : Pratik Daud
		// Date         : 06/04/2026
		// Purpose      : Synchronize maximize button state after each render cycle.
		// ==============================================
		// Changes Start
		this._syncMaximizeButton();
		//Changes End
	}
        
	// SteepGraph Modification
	// Developer    : Pratik Daud
	// Date         : 06/04/2026
	// Purpose      : Create/update maximize button and sync its state with accordion.
	// ==============================================
	// Changes Start
	_syncMaximizeButton() {
	    if (!this.hasAttribute('maximized')) return;

	    const header = this.querySelector('.aras-accordion__header');

	    // WAIT if header not ready
	    if (!header) {
	        requestAnimationFrame(() => this._syncMaximizeButton());
	        return;
	    }

	    const isMaximized = this.getAttribute('maximized') === 'true';

		//always set display, not just when maximized
		const toggleBtn = header.querySelector('.aras-accordion__toggle-button');
		if (toggleBtn) {
			toggleBtn.style.display = isMaximized ? 'none' : 'inline-block';
		}

	    let btn = header.querySelector('.aras-acc-a-button');
	    if (!btn) {
	        btn = document.createElement('button');
	        btn.className = 'aras-acc-a-button';
	        btn.addEventListener('click', () => this._maximize());

	        const tooltip = document.createElement('div');
	        tooltip.className = 'aras-tooltip';
	        tooltip.setAttribute('data-tooltip-pos', 'down-left');

	        const icon = document.createElement('div');
	        tooltip.appendChild(icon);
	        btn.appendChild(tooltip);
	        header.appendChild(btn);
	    }

	    const label = isMaximized ? 'Restore' : 'Maximize';
	    const iconClass = isMaximized
	        ? 'aras-acc-a-button-icon aras-icon-minimize'
	        : 'aras-acc-a-button-icon aras-icon-maximize';

	    btn.setAttribute('aria-label', label);
	    btn.classList.toggle('aras-acc-a-button_maximized', isMaximized);

	    const tooltip = btn.querySelector('.aras-tooltip');
	    if (tooltip) tooltip.setAttribute('data-tooltip', label);

	    const icon = btn.querySelector('div > div');
	    if (icon) icon.className = iconClass;
	}

	_maximize() {
		if (!this._readyForMaximize) return;

		const wrapper = this.closest('.aras-scroller__wrapper');
		if (!wrapper) return;

		const current = this.getAttribute('maximized') === 'true';
		const newValue = String(!current);

		this.setAttribute('maximized', newValue);

		// Force expand when maximizing
		if (newValue === 'true') {
			this.collapsed = false;
		}

		// Ensure UI sync AFTER render
		requestAnimationFrame(() => {
			this._syncMaximizeButton();
		});

		this.dispatchEvent(
			new CustomEvent('accordionMaximize', {
				bubbles: true,
				detail: { maximized: newValue }
			})
		);
	}
    //Changes End
	toggle(shouldCollapse) {
		if (this.getAttribute('maximized') === 'true') {
			this.setAttribute('maximized', 'false');
		}
		return new Promise((resolve) => {
			const toggleButton = this.querySelector('.aras-accordion__toggle-button');
			const accordionContent = this.querySelector('.aras-accordion__content');
            // SteepGraph Modification
			// Developer    : Pratik Daud
			// Date         : 06/04/2026
			// Purpose      : Add safety check for missing content and handle toggle button visibility when maximized.
			// ==============================================
			// Changes Start
			if (!accordionContent) {
				resolve();
				return;
			}
            //Changes End
			const handler = (event) => {
				if (event.target !== accordionContent) {
					return;
				}

				this.collapsed = shouldCollapse;
				accordionContent.style.height = '';
				this.dispatchEvent(
					new CustomEvent('accordionToggle', {
						bubbles: true
					})
				);
				accordionContent.removeEventListener('transitionend', handler);
				resolve();
			};

			shouldCollapse =
				typeof shouldCollapse === 'boolean' ? shouldCollapse : !this.collapsed;
			// SteepGraph Modification
			// Developer    : Pratik Daud
			// Date         : 06/04/2026
			// Purpose      : Null check - toggle button is hidden when maximized.Only if condition is added here other is OOTB.
			// ==============================================
			// Changes Start
			if (toggleButton) {
			//Changes End
				toggleButton.classList.toggle('aras-icon-arrow_up', !shouldCollapse);
				toggleButton.classList.toggle('aras-icon-arrow_down', shouldCollapse);
			}
                        
			accordionContent.addEventListener('transitionend', handler);

			if (shouldCollapse) {
				accordionContent.style.height = accordionContent.offsetHeight + 'px';
				accordionContent.offsetHeight; // Should reflow
				accordionContent.style.height = '0';
				return;
			}

			this.classList.remove('aras-accordion_collapsed');
			const contentHeight = accordionContent.offsetHeight;

			accordionContent.style.height = '0';
			accordionContent.offsetHeight;
			accordionContent.style.height = contentHeight + 'px';
		});
	}
}
